/**
 * RemoteConfigurationsContainer
 *
 * Main container for the Remote Configurations page.
 * Displays configurations in a table with search, filters, and side panel.
 */

var React = require('react');
var useState = React.useState;
var useMemo = React.useMemo;
var useEffect = React.useEffect;
var useCallback = React.useCallback;

var useRemoteConfigurations = require('./useRemoteConfigurations');
var RemoteConfigurationsTable = require('./RemoteConfigurationsTable');
var ConfigurationFilterBar = require('./ConfigurationFilterBar');
var ConfigurationDetailsPanel = require('./ConfigurationDetailsPanel');
var ConfigurationModal = require('./ConfigurationModal');
var ConfigurationPriorityInfo = require('./ConfigurationPriorityInfo');
var ConfigurationCelebration = require('./ConfigurationCelebration');
var {
  DEFAULT_FILTERS,
  TOAST_DURATION,
} = require('./RemoteConfigurations.types');
var { filterConfigurations } = require('./RemoteConfigurations.util');
var { trackPageView } = require('Utils');

require('./RemoteConfigurations.scss');

/**
 * RemoteConfigurationsContainer component
 */
var RemoteConfigurationsContainer = function (props) {
  // Data management hook
  var configsHook = useRemoteConfigurations();
  var configurations = configsHook.configurations;
  var loading = configsHook.loading;
  var error = configsHook.error;
  var refresh = configsHook.refresh;
  var createConfiguration = configsHook.createConfiguration;
  var updateConfiguration = configsHook.updateConfiguration;
  var deleteConfiguration = configsHook.deleteConfiguration;
  var toggleConfiguration = configsHook.toggleConfiguration;

  // UI State
  var filtersState = useState(DEFAULT_FILTERS);
  var filters = filtersState[0];
  var setFilters = filtersState[1];

  var selectedConfigIdState = useState(null);
  var selectedConfigId = selectedConfigIdState[0];
  var setSelectedConfigId = selectedConfigIdState[1];

  var modalState = useState({ isOpen: false, configuration: null });
  var modal = modalState[0];
  var setModal = modalState[1];

  var deleteConfirmState = useState({ isOpen: false, configuration: null });
  var deleteConfirm = deleteConfirmState[0];
  var setDeleteConfirm = deleteConfirmState[1];

  var toastState = useState({ show: false, type: '', message: '' });
  var toast = toastState[0];
  var setToast = toastState[1];

  var celebrationState = useState(false);
  var showCelebration = celebrationState[0];
  var setShowCelebration = celebrationState[1];

  // Track page view on mount
  useEffect(function () {
    trackPageView('Remote Configurations');
  }, []);

  // Filter configurations
  var filteredConfigurations = useMemo(
    function () {
      return filterConfigurations(configurations, filters);
    },
    [configurations, filters],
  );

  // Get selected configuration
  var selectedConfiguration = useMemo(
    function () {
      if (!selectedConfigId) {
        return null;
      }
      return (
        configurations.find(function (c) {
          return c.id === selectedConfigId;
        }) || null
      );
    },
    [configurations, selectedConfigId],
  );

  // Show toast notification
  var showToast = useCallback(function (type, message) {
    setToast({ show: true, type: type, message: message });
    setTimeout(function () {
      setToast({ show: false, type: '', message: '' });
    }, TOAST_DURATION);
  }, []);

  // Handlers
  var handleFilterChange = function (newFilters) {
    setFilters(newFilters);
  };

  var handleSelectConfig = function (config) {
    setSelectedConfigId(config.id);
  };

  var handleClosePanel = function () {
    setSelectedConfigId(null);
  };

  var handleAddNew = function () {
    setModal({ isOpen: true, configuration: null });
  };

  var handleEdit = function (config) {
    setModal({ isOpen: true, configuration: config });
  };

  var handleCloseModal = function () {
    setModal({ isOpen: false, configuration: null });
  };

  var handleSave = function (configData, isEdit) {
    // Check if default_value is being set to true
    var newDefaultValue =
      configData.default_value === true || configData.default_value === 'true';

    // Get old value only for edits with valid configuration
    var oldDefaultValue = false;
    if (isEdit && modal.configuration) {
      oldDefaultValue =
        modal.configuration.default_value === true ||
        modal.configuration.default_value === 'true';
    }

    // Celebrate when: creating with enabled OR editing from disabled to enabled
    var shouldCelebrate = newDefaultValue && (!isEdit || !oldDefaultValue);

    var savePromise = isEdit
      ? updateConfiguration(configData)
      : createConfiguration(configData);

    return savePromise
      .then(function () {
        showToast(
          'success',
          isEdit
            ? 'Configuration updated successfully'
            : 'Configuration created successfully',
        );
        if (shouldCelebrate) {
          setShowCelebration(true);
        }
      })
      .catch(function () {
        showToast(
          'error',
          isEdit
            ? 'Failed to update configuration'
            : 'Failed to create configuration',
        );
        throw new Error(isEdit ? 'Update failed' : 'Create failed');
      });
  };

  var handleToggle = function (config) {
    toggleConfiguration(config)
      .then(function (updatedConfig) {
        // Use the actual value from the updated config for accurate messaging
        var isNowEnabled =
          updatedConfig.default_value === true ||
          updatedConfig.default_value === 'true';
        showToast(
          'success',
          'Configuration ' + (isNowEnabled ? 'enabled' : 'disabled'),
        );
        // Only celebrate when enabling (turning ON), not when disabling
        if (isNowEnabled) {
          setShowCelebration(true);
        }
      })
      .catch(function () {
        showToast('error', 'Failed to toggle configuration');
      });
  };

  var handleCelebrationComplete = function () {
    setShowCelebration(false);
  };

  var handleDeleteClick = function (config) {
    setDeleteConfirm({ isOpen: true, configuration: config });
  };

  var handleConfirmDelete = function () {
    var config = deleteConfirm.configuration;
    if (!config) {
      return;
    }

    deleteConfiguration(config.id)
      .then(function () {
        showToast('success', 'Configuration deleted successfully');
        if (selectedConfigId === config.id) {
          setSelectedConfigId(null);
        }
      })
      .catch(function () {
        showToast('error', 'Failed to delete configuration');
      })
      .finally(function () {
        setDeleteConfirm({ isOpen: false, configuration: null });
      });
  };

  var handleCancelDelete = function () {
    setDeleteConfirm({ isOpen: false, configuration: null });
  };

  // Render toast notification
  var renderToast = function () {
    if (!toast.show) {
      return null;
    }

    return React.createElement(
      'div',
      {
        className: 'remote-config-toast remote-config-toast--' + toast.type,
      },
      [
        React.createElement(
          'span',
          {
            key: 'icon',
            className: 'remote-config-toast__icon',
          },
          toast.type === 'success'
            ? React.createElement(
                'svg',
                {
                  width: 20,
                  height: 20,
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 2,
                },
                [
                  React.createElement('path', {
                    key: 'p1',
                    d: 'M22 11.08V12a10 10 0 1 1-5.93-9.14',
                  }),
                  React.createElement('polyline', {
                    key: 'p2',
                    points: '22 4 12 14.01 9 11.01',
                  }),
                ],
              )
            : React.createElement(
                'svg',
                {
                  width: 20,
                  height: 20,
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 2,
                },
                [
                  React.createElement('circle', {
                    key: 'c',
                    cx: 12,
                    cy: 12,
                    r: 10,
                  }),
                  React.createElement('line', {
                    key: 'l1',
                    x1: 15,
                    y1: 9,
                    x2: 9,
                    y2: 15,
                  }),
                  React.createElement('line', {
                    key: 'l2',
                    x1: 9,
                    y1: 9,
                    x2: 15,
                    y2: 15,
                  }),
                ],
              ),
        ),
        React.createElement(
          'span',
          {
            key: 'message',
            className: 'remote-config-toast__message',
          },
          toast.message,
        ),
      ],
    );
  };

  // Render delete confirmation dialog
  var renderDeleteConfirm = function () {
    if (!deleteConfirm.isOpen) {
      return null;
    }

    return React.createElement(
      'div',
      {
        className: 'remote-config-modal-overlay',
      },
      React.createElement(
        'div',
        {
          className: 'remote-config-modal',
          style: { maxWidth: '400px' },
        },
        React.createElement(
          'div',
          {
            className: 'remote-config-confirm',
          },
          [
            React.createElement('h3', { key: 'title' }, 'Delete Configuration'),
            React.createElement(
              'p',
              { key: 'message' },
              'Are you sure you want to delete "' +
                (deleteConfirm.configuration &&
                  deleteConfirm.configuration.configuration_name) +
                '"? This action cannot be undone.',
            ),
            React.createElement(
              'div',
              {
                key: 'actions',
                className: 'remote-config-confirm__actions',
              },
              [
                React.createElement(
                  'button',
                  {
                    key: 'cancel',
                    className: 'remote-config-btn-secondary',
                    onClick: handleCancelDelete,
                  },
                  'Cancel',
                ),
                React.createElement(
                  'button',
                  {
                    key: 'confirm',
                    className: 'remote-config-btn-danger',
                    onClick: handleConfirmDelete,
                  },
                  'Delete',
                ),
              ],
            ),
          ],
        ),
      ),
    );
  };

  // Loading state
  if (loading) {
    return React.createElement(
      'div',
      {
        className: 'remote-config-container',
      },
      React.createElement(
        'div',
        {
          className: 'remote-config-loading',
        },
        [
          React.createElement(
            'div',
            {
              key: 'spinner',
              className: 'spinner-border text-grey71',
              role: 'status',
              style: { width: '2rem', height: '2rem' },
            },
            React.createElement('span', { className: 'sr-only' }, 'Loading...'),
          ),
          React.createElement(
            'p',
            { key: 'text' },
            'Loading configurations...',
          ),
        ],
      ),
    );
  }

  // Error state
  if (error) {
    return React.createElement(
      'div',
      {
        className: 'remote-config-container',
      },
      React.createElement(
        'div',
        {
          className: 'remote-config-error',
        },
        [
          React.createElement('p', { key: 'message' }, error),
          React.createElement(
            'button',
            {
              key: 'retry',
              className: 'remote-config-btn-primary',
              onClick: refresh,
            },
            'Try Again',
          ),
        ],
      ),
    );
  }

  var contentClassName = 'remote-config-content';
  if (selectedConfigId) {
    contentClassName += ' remote-config-content--with-panel';
  }

  return React.createElement(
    'div',
    {
      className: 'remote-config-container',
    },
    [
      // Toast notification
      renderToast(),

      // Delete confirmation dialog
      renderDeleteConfirm(),

      // Celebration animation
      React.createElement(ConfigurationCelebration, {
        key: 'celebration',
        show: showCelebration,
        onComplete: handleCelebrationComplete,
      }),

      // Configuration modal
      React.createElement(ConfigurationModal, {
        key: 'modal',
        isOpen: modal.isOpen,
        configuration: modal.configuration,
        onClose: handleCloseModal,
        onSave: handleSave,
      }),

      // Header
      React.createElement(
        'div',
        {
          key: 'header',
          className: 'remote-config-header',
        },
        [
          React.createElement('h1', { key: 'title' }, 'Remote Configurations'),
          React.createElement(
            'div',
            {
              key: 'actions',
              className: 'remote-config-header__actions',
            },
            React.createElement(
              'button',
              {
                className: 'remote-config-btn-primary',
                onClick: handleAddNew,
              },
              [
                React.createElement(
                  'svg',
                  {
                    key: 'icon',
                    width: 18,
                    height: 18,
                    viewBox: '0 0 24 24',
                    fill: 'none',
                    stroke: 'currentColor',
                    strokeWidth: 2,
                  },
                  [
                    React.createElement('line', {
                      key: 'l1',
                      x1: 12,
                      y1: 5,
                      x2: 12,
                      y2: 19,
                    }),
                    React.createElement('line', {
                      key: 'l2',
                      x1: 5,
                      y1: 12,
                      x2: 19,
                      y2: 12,
                    }),
                  ],
                ),
                React.createElement('span', { key: 'text' }, 'Add New'),
              ],
            ),
          ),
        ],
      ),

      // Priority info section
      React.createElement(ConfigurationPriorityInfo, {
        key: 'priority-info',
      }),

      // Filter bar
      React.createElement(ConfigurationFilterBar, {
        key: 'filters',
        filters: filters,
        onFilterChange: handleFilterChange,
      }),

      // Main content (table + optional panel)
      React.createElement(
        'div',
        {
          key: 'content',
          className: contentClassName,
        },
        [
          // Table
          React.createElement(RemoteConfigurationsTable, {
            key: 'table',
            configurations: filteredConfigurations,
            selectedConfigId: selectedConfigId,
            onSelectConfig: handleSelectConfig,
            onToggle: handleToggle,
            onEdit: handleEdit,
            onDelete: handleDeleteClick,
          }),

          // Details panel
          selectedConfiguration &&
            React.createElement(ConfigurationDetailsPanel, {
              key: 'panel',
              configuration: selectedConfiguration,
              onClose: handleClosePanel,
              onEdit: handleEdit,
              onDelete: handleDeleteClick,
            }),
        ],
      ),
    ],
  );
};

module.exports = {
  RemoteConfigurationsContainer: RemoteConfigurationsContainer,
};
